import React, { useState } from 'react';
import {
  FiSearch,
  FiFilter,
  FiDownload,
} from 'react-icons/fi';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import { DataTable, type DataTableColumn } from '@/features/onboarding/components/DataTable';
import { LogsFilterModal, type LogsFilterData } from '@/features/onboarding/components/LogsFilterModal';

interface LogEntry {
  id: string;
  status: 'Aprovado' | 'Reprovado' | 'Recusou';
  user: string;
  date: string;
  reason: string;
}

export const LogsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const mockData: LogEntry[] = Array.from({ length: 64 }, (_, i) => ({
    id: `#456789`,
    status: i % 3 === 0 ? 'Aprovado' : i % 3 === 1 ? 'Reprovado' : 'Recusou',
    user: 'Carlos Rodrigues',
    date: 'DD/MM/AAAA - HH:MM:SS',
    reason: 'Lorem Ipsum',
  }));

  const handleApplyFilters = (_filterData: LogsFilterData) => {
    // Aplicar lógica de filtros aqui
    // filterData contém: { startDate, endDate, status, analyst }
    setIsFilterModalOpen(false);
  };

  const columns: DataTableColumn<LogEntry>[] = [
    {
      key: 'status',
      label: 'Status',
      width: '15%',
      render: (value: unknown) => {
        const status = value as 'Aprovado' | 'Reprovado' | 'Recusou';
        const colors = {
          Aprovado: 'bg-green-100 text-green-700',
          Reprovado: 'bg-red-100 text-red-700',
          Recusou: 'bg-orange-100 text-orange-700',
        };
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
            {status}
          </span>
        );
      },
    },
    {
      key: 'id',
      label: 'ID',
      width: '15%',
    },
    {
      key: 'user',
      label: 'Usuário',
      width: '20%',
      render: (value: unknown) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-xs text-purple-600 font-medium">CR</span>
          </div>
          <span>{value as string}</span>
        </div>
      ),
    },
    {
      key: 'date',
      label: 'Data',
      width: '25%',
    },
    {
      key: 'reason',
      label: 'Motivo',
      width: '25%',
    },
  ];

  // Pagination
  const totalPages = Math.ceil(mockData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = mockData.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      {/* Main Content */}
      <main className="p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-purple-600 mb-6">Log do sistema</h1>

            {/* Search and Filter Bar */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Buscar"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); }}
                  className="w-full py-3 pl-12 pr-4 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={() => { setIsFilterModalOpen(true); }}
                className="flex items-center gap-2 px-4 py-3 bg-white border-2 border-purple-600 text-neutral-700 rounded-lg font-medium hover:bg-purple-50 transition-colors"
              >
                <FiFilter className="w-5 h-5" />
                Filtros
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                <FiDownload className="w-5 h-5" />
                Exportar
              </button>
            </div>

            {/* Active Filters */}
            <div className="mb-6">
              <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Filtro (5)
              </span>
            </div>

            <DataTable
              columns={columns}
              data={currentData}
              totalItems={mockData.length}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </main>

      {/* Filter Modal */}
      <LogsFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => { setIsFilterModalOpen(false); }}
        onApply={handleApplyFilters}
      />
    </div>
  );
};
